import { FC, useMemo } from 'react';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { dateFormat, i18nTB } from 'utils/shared/utils';
import { TripPreviewSlotView } from 'utils/shared/tripResultBuilderPreview';

type ItineraryStop = {
  key: string;
  city: string;
  checkIn: string;
  checkOut: string;
  nights: number | null;
};

const formatShortDate = (value: string, locale: string) =>
  value ? dateFormat(value, 'd.M.yy', locale) : '-';

const buildItineraryStops = (slots: TripPreviewSlotView[]): ItineraryStop[] =>
  slots.flatMap((slot) =>
    slot.chainNodes.map((node) => ({
      key: node.slotKey,
      city: node.city,
      checkIn: node.checkIn,
      checkOut: node.checkOut,
      nights:
        node.hotelSection?.hotels.find((hotel) => hotel.isActive)?.nights ??
        node.hotelSection?.hotels[0]?.nights ??
        null,
    })),
  );

type TripBuilderClientItinerary2Props = {
  slots: TripPreviewSlotView[];
};

const TripBuilderClientItinerary2: FC<TripBuilderClientItinerary2Props> = ({ slots }) => {
  const { t, i18n } = useTranslation(undefined, { i18n: i18nTB });
  const stops = useMemo(() => buildItineraryStops(slots), [slots]);

  if (!stops.length) {
    return null;
  }

  return (
    <section className="tb2-itinerary" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
      <h2 className="tb2-itinerary-title">{t('your_itinerary')}</h2>
      <div className="tb2-itinerary-track">
        <div className="tb2-itinerary-line" aria-hidden="true" />
        {stops.map((stop) => (
          <article className="tb2-itinerary-stop" key={stop.key}>
            <div className="tb2-itinerary-stop-text">
              <div className="tb2-itinerary-stop-city">
                {stop.city}
                {stop.nights != null && (
                  <>
                    {' '}
                    ({stop.nights} {stop.nights === 1 ? t('night') : t('nights')})
                  </>
                )}
              </div>
              <div className="tb2-itinerary-stop-dates">
                {formatShortDate(stop.checkIn, i18n.language)} - {formatShortDate(stop.checkOut, i18n.language)}
              </div>
            </div>
            <FontAwesomeIcon icon={faLocationDot} className="tb2-itinerary-stop-icon" aria-hidden="true" />
          </article>
        ))}
      </div>
    </section>
  );
};

export default TripBuilderClientItinerary2;
